import type { ExternalTool } from '@tunasong/manifest'
import { Add, Delete, OpenLink } from '@tunasong/icons'
import {
  useUserConfig,
  Button,
  IconButton,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'
import styles from './external-tools-config.module.css'

const defaultTool: ExternalTool = { name: '', url: '' }

const normalizeUrl = (url: string): string => {
  const trimmed = url.trim()
  if (trimmed && !/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`
  }
  return trimmed
}

const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return /^https?:$/i.test(parsed.protocol)
  } catch {
    return false
  }
}

export const ExternalToolsConfig: FC = () => {
  const [externalTools = [], updateExternalTools] = useUserConfig('externalTools')
  const [newTool, setNewTool] = useState<ExternalTool>(defaultTool)
  const [urlError, setUrlError] = useState<string | undefined>(undefined)

  const handleAdd = () => {
    if (!newTool.name.trim() || !newTool.url.trim()) {
      return
    }
    const normalized = { ...newTool, url: normalizeUrl(newTool.url) }
    if (!isValidUrl(normalized.url)) {
      setUrlError('Please enter a valid URL (e.g., https://example.com)')
      return
    }
    setUrlError(undefined)
    updateExternalTools([...externalTools, normalized])
    setNewTool(defaultTool)
  }

  const handleRemove = (index: number) => {
    updateExternalTools(externalTools.filter((_, i) => i !== index))
  }

  return (
    <div>
      <Typography variant="h6" className={styles.sectionTitle}>
        External Tools
      </Typography>
      <Typography variant="body2" color="textSecondary" className={styles.description}>
        Add links to external tools and services. These will appear in the Tools section of the sidebar for quick
        access.
      </Typography>

      {externalTools.length > 0 ? (
        <Table className={styles.table}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>URL</TableCell>
              <TableCell width={50} />
            </TableRow>
          </TableHead>
          <TableBody>
            {externalTools.map((tool, index) => (
              <TableRow key={index}>
                <TableCell>{tool.name}</TableCell>
                <TableCell>
                  <div className={styles.urlCell}>
                    <Typography variant="body2" className={styles.urlText}>
                      {tool.url}
                    </Typography>
                    <IconButton size="small" href={tool.url} target="_blank" rel="noopener noreferrer">
                      <OpenLink size={16} />
                    </IconButton>
                  </div>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleRemove(index)}>
                    <Delete size={16} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="textSecondary" className={styles.noItems}>
          No external tools added yet.
        </Typography>
      )}

      <div className={styles.formRow}>
        <TextField
          size="small"
          label="Name"
          placeholder="e.g., My DAW"
          value={newTool.name}
          onChange={e => setNewTool({ ...newTool, name: e.target.value })}
          className={styles.nameField}
        />
        <TextField
          size="small"
          label="URL"
          placeholder="https://example.com"
          value={newTool.url}
          error={!!urlError}
          helperText={urlError}
          onChange={e => {
            setNewTool({ ...newTool, url: e.target.value })
            if (urlError) {
              setUrlError(undefined)
            }
          }}
          className={styles.urlField}
        />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAdd}
          disabled={!newTool.name.trim() || !newTool.url.trim()}
        >
          Add
        </Button>
      </div>
    </div>
  )
}

export default ExternalToolsConfig
